import {
  CLOSEOUT_KEYS,
  closeoutEmail,
  closeoutReview,
  jobAddress,
  readyToSend,
  type CloseoutKey,
  type Job,
  type JobChecklistItem,
  type Profile,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import {
  fetchCloseoutInput,
  noteCloseoutEmail,
  sendJobToCloseout,
} from "../../lib/closeout";
import { fmtDate } from "../../lib/format";
import { sendEmail } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import { jobLink } from "../../lib/jobLink";
import { useSession } from "../../session";
import { useGmailAccount } from "../email/useGmailAccount";

/**
 * Send to Kelsey — the five questions before a finished job leaves its rep.
 *
 * Jonathon, 2026-10-06: "are all of the trades correct (and it needs to show
 * them what might not be), is the estimate amount correct, are the work orders
 * accurate and complete, are the material orders accurate, are the other
 * expenses accurate and complete. Once they have verified all of that then they
 * can hit send."
 *
 * So each question shows what the job says and what might be wrong, and has a
 * box the rep ticks to say it is right. Send waits for all five. The warnings
 * never block it — they are what is being confirmed — and what was showing is
 * kept with the send, and goes in the email (ADR 0273).
 *
 * The email leaves the sender's own Gmail. Without one connected the job still
 * moves; Kelsey's board is the record, the email only the nudge.
 */
export function SendToCloseoutModal({
  job,
  items,
  owner,
  onClose,
  onSent,
}: {
  job: Job;
  items: readonly JobChecklistItem[];
  /** Who it goes to — Kelsey. */
  owner: Profile;
  onClose: () => void;
  /** After the send, with a line to show if the email did not go. */
  onSent: (note: string | null) => Promise<void>;
}) {
  const { displayName } = useSession();
  const { account } = useGmailAccount();
  const firstName = owner.full_name?.split(" ")[0] ?? "Kelsey";

  const query = useAsync(
    () => fetchCloseoutInput(job, items),
    [job.id, items.length],
  );
  const review = useMemo(
    () => (query.data ? closeoutReview(query.data) : null),
    [query.data],
  );

  const [ticked, setTicked] = useState<ReadonlySet<CloseoutKey>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = Boolean(review) && readyToSend(ticked) && !busy;

  function toggle(key: CloseoutKey) {
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function send() {
    if (!review || !ready) return;
    setBusy(true);
    setError(null);
    try {
      await sendJobToCloseout(job.id, CLOSEOUT_KEYS, review.warnings);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      return;
    }

    // The job has moved. From here on nothing undoes that; the email is
    // tried, and whatever happens to it is written down and said.
    let note: string | null = null;
    if (!owner.email) {
      note = `${owner.full_name} has no email address, so no email went. The job is on ${firstName}'s board.`;
    } else if (!account) {
      note = `Your Gmail is not connected, so ${firstName} was not emailed. The job is on ${firstName}'s board.`;
    } else {
      const email = closeoutEmail({
        review,
        customer: job.contact?.full_name ?? "Customer",
        address: jobAddress(job),
        jobNumber: job.job_number ?? null,
        repName: job.rep?.full_name ?? null,
        sentBy: displayName,
        sentOn: fmtDate(new Date().toISOString()),
        link: `${window.location.origin}/#${jobLink(job.id)}`,
      });
      try {
        await sendEmail({
          to: owner.email,
          subject: email.subject,
          text: email.text,
          jobId: job.id,
          internal: true,
        });
      } catch (e) {
        note = `The job is on ${firstName}'s board, but the email failed: ${
          e instanceof Error ? e.message : String(e)
        }`;
      }
    }
    await noteCloseoutEmail(job.id, note).catch(() => {});
    await onSent(note);
  }

  return (
    <Modal
      title={t("Send to {name}", { name: firstName })}
      subtitle={t(
        "Check each one, tick it, then send. The job leaves your board for {name}'s.",
        { name: firstName },
      )}
      onClose={onClose}
      roomy
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={!ready}
            style={ready ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
            onClick={() => void send()}
          >
            {busy ? t("Sending…") : t("Send")}
          </button>
        </>
      }
    >
      <ErrorNote error={error ?? query.error ?? null} />
      {!review ? (
        <div className="stub">{t("Checking the job…")}</div>
      ) : (
        <>
          {!account && (
            <div className="config-warn" role="status">
              {t(
                "Your Gmail is not connected, so {name} will not get an email. The job still goes to {name}'s board.",
                { name: firstName },
              )}
            </div>
          )}
          {review.general.map((text) => (
            <div className="config-warn" role="status" key={text}>
              {t(text)}
            </div>
          ))}
          {review.sections.map((s) => (
            <div className="closeout-q" key={s.key}>
              <label>
                <input
                  type="checkbox"
                  checked={ticked.has(s.key)}
                  onChange={() => toggle(s.key)}
                />
                {t(s.question)}
              </label>
              <ul>
                {s.facts.map((fact, i) => (
                  <li key={`f${i}`}>{fact}</li>
                ))}
                {s.warnings.map((warning, i) => (
                  <li key={`w${i}`} className="warn">
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}
